<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NervenetEmbedTokenAPI from 'dashboard/api/nervenetEmbedToken';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  // Token-free Nervenet URL. This component appends the `cw` embed token itself,
  // so every embedded page gets one — callers must not append their own.
  src: {
    type: String,
    required: true,
  },
  // Show the "taking longer than usual" hint after this many ms.
  slowThreshold: {
    type: Number,
    default: 8000,
  },
});

const { t } = useI18n();

const loading = ref(true);
const slow = ref(false);
// Nervenet verifies this token before serving any /accounts/:id/* page — without
// it the request 404s. Minted here rather than in each page component so no embed
// page can ship without one, and minted fresh per mount rather than cached so it
// is never stale by the time the iframe actually loads.
const embedToken = ref('');
const tokenFailed = ref(false);
// Bumping this key remounts the iframe to force a fresh load (retry).
const reloadKey = ref(0);
// Nervenet pages too narrow to host their own modals in place (the
// conversation bill panel, ~296px) ask to take over the full tab instead —
// see the message contract below. This is a viewport takeover within the
// current browser tab, not window.open/Fullscreen API: no new tab, no OS
// fullscreen, Chatwoot's own chrome (address bar, conversation list) stays
// reachable by scrolling/resizing the window, it's just visually covered
// while a picker is open.
const expanded = ref(false);

let slowTimer = null;

const origin = computed(() => {
  try {
    return new URL(props.src).origin;
  } catch {
    return null;
  }
});

const frameSrc = computed(() => {
  if (!embedToken.value) return '';
  try {
    const url = new URL(props.src);
    // searchParams, never string concatenation: a page URL that already carries a
    // query string would otherwise swallow the second '?' into the token's value
    // and fail auth with a silent 404.
    url.searchParams.set('cw', embedToken.value);
    return url.toString();
  } catch {
    return '';
  }
});

// event.origin must match this exactly, or any embedded/opened window from
// anywhere could ask us to cover the screen.
const handleMessage = event => {
  if (!origin.value || event.origin !== origin.value) return;
  if (event.data?.source !== 'nervenet-commerce-picker') return;
  expanded.value = !!event.data.expanded;
};

const clearSlowTimer = () => {
  if (slowTimer) {
    clearTimeout(slowTimer);
    slowTimer = null;
  }
};

const startLoading = () => {
  loading.value = true;
  slow.value = false;
  clearSlowTimer();
  slowTimer = setTimeout(() => {
    if (loading.value) slow.value = true;
  }, props.slowThreshold);
};

const onLoad = () => {
  loading.value = false;
  slow.value = false;
  clearSlowTimer();
};

const fetchToken = async () => {
  tokenFailed.value = false;
  try {
    const response = await NervenetEmbedTokenAPI.create();
    embedToken.value = response.data.token;
  } catch (error) {
    // Surface it. Failing silently here used to leave a permanently blank panel
    // with nothing in the console to explain it.
    embedToken.value = '';
    tokenFailed.value = true;
  }
};

const onRetry = () => {
  reloadKey.value += 1;
  startLoading();
  fetchToken();
};

// Open the connection to Nervenet early so the iframe request is faster.
let preconnectEl = null;
const addPreconnect = () => {
  if (!origin.value || preconnectEl) return;
  preconnectEl = document.createElement('link');
  preconnectEl.rel = 'preconnect';
  preconnectEl.href = origin.value;
  preconnectEl.crossOrigin = 'use-credentials';
  document.head.appendChild(preconnectEl);
};

watch(
  () => props.src,
  () => {
    expanded.value = false;
    startLoading();
  }
);

onMounted(() => {
  addPreconnect();
  startLoading();
  fetchToken();
  window.addEventListener('message', handleMessage);
});

onBeforeUnmount(() => {
  clearSlowTimer();
  if (preconnectEl) {
    preconnectEl.remove();
    preconnectEl = null;
  }
  window.removeEventListener('message', handleMessage);
});
</script>

<template>
  <div class="relative w-full h-full">
    <iframe
      v-if="frameSrc"
      :key="reloadKey"
      :src="frameSrc"
      class="w-full h-full border-0"
      :class="expanded ? 'fixed inset-0 z-[9999]' : ''"
      @load="onLoad"
    />

    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="loading || tokenFailed"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-n-background"
      >
        <template v-if="tokenFailed">
          <span class="i-lucide-triangle-alert size-7 text-n-slate-10" />
          <p class="text-sm text-n-slate-11 m-0">
            {{ t('NERVENET_FRAME.TOKEN_ERROR') }}
          </p>
        </template>
        <template v-else>
          <Spinner :size="28" class="text-n-brand" />
          <p class="text-sm text-n-slate-11 m-0">
            {{ t('NERVENET_FRAME.LOADING') }}
          </p>
        </template>
        <template v-if="slow || tokenFailed">
          <p v-if="slow && !tokenFailed" class="text-xs text-n-slate-10 m-0">
            {{ t('NERVENET_FRAME.SLOW') }}
          </p>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-n-weak text-n-slate-12 hover:bg-n-alpha-1 transition-colors duration-150"
            @click="onRetry"
          >
            <span class="i-lucide-rotate-cw size-3.5" />
            {{ t('NERVENET_FRAME.RETRY') }}
          </button>
        </template>
      </div>
    </transition>

    <slot />
  </div>
</template>
